/**
 * @file App.jsx
 * @description Zentrale Anwendungskomponente und Routing-Definition von ThekenOS.
 *
 * Wie die Seitennavigation aufgebaut ist:
 * 1. **Code-Splitting / Lazy Loading**: Seitenkomponenten werden erst dann aus dem Netzwerk geladen,
 *    wenn der Nutzer sie tatsächlich ansteuert (verbessert die Ladezeit auf Mobilgeräten).
 * 2. **Öffentliche Routen**: `/login` (Anmeldung).
 * 3. **Geschützte Routen (`PrivateRoute`)**: Alle Seiten im Hauptbereich setzen einen erfolgreichen Login
 *    und die passende Benutzerrolle voraus (z. B. nur Admins/Redakteure für Vorlagenverwaltung).
 * 4. **Rollenbasierte Weiterleitungen**: Reine Einkäufer werden automatisch auf `/einkauf` geleitet,
 *    alle anderen Rollen auf das `/dashboard`.
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { canAccessEinkauf } from './config/security';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';

// Asynchron geladene Seitenkomponenten (Code-Splitting zur Performanz-Optimierung)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DailyTodosPage = lazy(() => import('./pages/DailyTodosPage'));
const ThekipediaPage = lazy(() => import('./pages/ThekipediaPage'));
const ConversationNotesPage = lazy(() => import('./pages/ConversationNotesPage'));
const TemplateAdminPage = lazy(() => import('./pages/TemplateAdminPage'));
const ThekenMailViewer = lazy(() => import('./pages/ThekenMailViewer'));
const UserAdminPage = lazy(() => import('./pages/UserAdminPage'));
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const ShoppingListPage = lazy(() => import('./pages/ShoppingListPage'));


/**
 * Fallback-Ladeanzeige während des asynchronen Nachladens einer Seite.
 * @returns {JSX.Element} Lade-Indikator
 */
const PageLoader = () => (
  <div className="global-loading-container">Lade Seite…</div>
);

/**
 * Hauptkomponente der App mit Routing-Tabelle und rollenbasierter Zugriffslogik.
 * @returns {JSX.Element} Das gerenderte Routen-Set der Anwendung
 */
function App() {
  const { role } = useAuth();
  // Alle registrierten Rollen, die Zugriff auf den geschützten Hauptbereich haben
  const allRoles = ['Theki', 'Redakteur', 'Admin', 'System-Admin', 'Einkäufer', 'Beta-Theki'];

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Öffentliche Routen: Login */}
        <Route path="/login" element={<LoginPage />} />

        {/* Geschützter Bereich im globalen Anwendungs-Layout (Kopfzeile + Navigation) */}
        <Route element={<PrivateRoute allowedRoles={allRoles}><Layout /></PrivateRoute>}>
          {/* Basis-Routen: automatische Weiterleitung für reine Einkäufer */}
          <Route path="/" element={<Navigate to={role === 'Einkäufer' ? '/einkauf' : '/dashboard'} replace />} />
          <Route path="/dashboard" element={
            role === 'Einkäufer' ? <Navigate to="/einkauf" replace /> : <Dashboard />
          } />
          <Route path="/todos" element={<DailyTodosPage />} />
          <Route path="/thekipedia/*" element={<ThekipediaPage />} />
          
          {/* Einkaufsliste: Admin, System-Admin, Einkäufer sowie Redakteure MIT Einkaufsrecht.
              Die Regel steht in `canAccessEinkauf` und wird von der Sidebar identisch genutzt. */}
          <Route path="/einkauf" element={
            <PrivateRoute allow={canAccessEinkauf}>
              <ShoppingListPage />
            </PrivateRoute>
          } />

          {/* Schicht-Notizen und ThekenMail-Viewer */}
          <Route path="/notes" element={<ConversationNotesPage />} />
          <Route path="/mail" element={<ThekenMailViewer />} />

          {/* Erweiterte Berechtigungen: Feedback-System und Aufgaben-Vorlagenverwaltung */}
          <Route path="/feedback" element={
            <PrivateRoute allowedRoles={['Redakteur', 'Admin', 'System-Admin', 'Beta-Theki']}>
              <FeedbackPage />
            </PrivateRoute>
          } />
          <Route path="/admin/templates" element={
            <PrivateRoute allowedRoles={['Redakteur', 'Admin', 'System-Admin']}>
              <TemplateAdminPage />
            </PrivateRoute>
          } />

          {/* Höchste Berechtigungsstufe: Alle Admin-Tools zentral unter den System-Einstellungen */}
          <Route path="/admin/users" element={
            <PrivateRoute allowedRoles={['Admin', 'System-Admin']}>
              <Navigate to="/admin/settings?tab=users" replace />
            </PrivateRoute>
          } />
          <Route path="/admin/settings" element={
            <PrivateRoute allowedRoles={['Admin', 'System-Admin']}>
              <SystemSettingsPage />
            </PrivateRoute>
          } />
        </Route>

        {/* Fallback für nicht existierende URLs: Weiterleitung zur Startseite */}
        <Route path="*" element={<Navigate to={role === 'Einkäufer' ? '/einkauf' : '/dashboard'} replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
